import type { Tenant } from "@/hooks/useTenant";
import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { formatZodError } from "@Simplicity-Education/core";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  FormDialog,
  Input,
  Label,
} from "@simplicity/ui";
import { ExternalLink, Plus, RotateCcw, School, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

// SIM-2320: a soft-deleted tenant carries the removal timestamp. Mirror of the
// server-side `deletedTenantSchema` in tenants.contract.ts (apps/portal cannot
// import from apps/platform — monorepo boundary). `deletedAt` arrives as an ISO
// string over the wire.
interface DeletedTenant extends Tenant {
  deletedAt: string;
}

// Mirror of the server-side `TenantDeletionPreview` contract in
// apps/platform/src/orpc/routes/portal/tenants/tenants.contract.ts. Defined
// locally because apps/portal cannot import from apps/platform (monorepo
// boundary). The shape is verified end-to-end by the portal-api E2E suite
// (seed rows → fetch → assert counts).
interface TenantDeletionPreview {
  dashboards: number;
  users: number;
  groups: number;
  dottieSessions: number;
  dottieMessages: number;
  biCredentials: number;
  googleOAuthConnections: number;
  settings: number;
  allowedDomains: number;
  analyticsEvents: number;
  helpRequests: number;
  permissionSyncLogs: number;
  sparkDashboards: number;
}

interface DeleteDialogState {
  tenantId: string;
  tenantName: string;
  preview: TenantDeletionPreview | null;
  loading: boolean;
  previewError: string | null;
}

function plural(n: number, singular: string, pluralForm = `${singular}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? singular : pluralForm}`;
}

// Preview rows in display order. Most are a single count → one row, described by
// a (key, singular, optional plural). The Dottie chat/message row folds two counts
// into one line, so it carries a custom `render`. Driven by a table so `previewRows`
// stays under the complexity cap — adding a category is a data edit, not a branch.
type PreviewCategory =
  | { key: keyof TenantDeletionPreview; singular: string; plural?: string; render?: never }
  | { key: keyof TenantDeletionPreview; render: (p: TenantDeletionPreview) => string };

const PREVIEW_CATEGORIES: ReadonlyArray<PreviewCategory> = [
  { key: "dashboards", singular: "dashboard" },
  { key: "users", singular: "user membership" },
  { key: "groups", singular: "user group" },
  {
    key: "dottieSessions",
    render: (p) =>
      `${plural(p.dottieSessions, "Dottie chat")} (${plural(p.dottieMessages, "message")})`,
  },
  { key: "biCredentials", singular: "BigQuery credential" },
  { key: "googleOAuthConnections", singular: "Google OAuth connection" },
  { key: "settings", singular: "tenant setting" },
  { key: "allowedDomains", singular: "allowed sign-in domain" },
  { key: "analyticsEvents", singular: "analytics event" },
  { key: "helpRequests", singular: "help request" },
  {
    key: "permissionSyncLogs",
    singular: "permission sync log entry",
    plural: "permission sync log entries",
  },
];

/**
 * Build the display rows for the deletion preview dialog. Only categories
 * with non-zero counts are surfaced so the list scales gracefully from a
 * fresh empty tenant (zero rows) to a long-lived production tenant (every
 * category populated). Order matches the pre-refactor list exactly.
 */
function previewRows(preview: TenantDeletionPreview): string[] {
  const rows: string[] = [];
  for (const category of PREVIEW_CATEGORIES) {
    if (preview[category.key] <= 0) continue;
    rows.push(
      category.render
        ? category.render(preview)
        : plural(preview[category.key], category.singular, category.plural),
    );
  }
  return rows;
}

interface TenantRowProps {
  tenant: Tenant;
  onEdit: (tenant: Tenant) => void;
  onNavigate: (slug: string) => void;
  onDelete: (tenantId: string) => void;
}

function TenantRow({ tenant, onEdit, onNavigate, onDelete }: TenantRowProps) {
  return (
    <li className="flex items-center justify-between px-4 py-3 text-sm">
      {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- list-item click surface that also contains nested Button interactives; a semantic <button> would nest interactives. */}
      <div
        role="button"
        tabIndex={0}
        className="flex items-center gap-3 flex-1 cursor-pointer"
        onClick={() => onEdit(tenant)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit(tenant);
          }
        }}
      >
        <School className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{tenant.name}</span>
        <span className="text-muted-foreground text-xs">{tenant.slug}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto w-auto p-1 text-muted-foreground hover:text-primary"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(tenant.slug);
          }}
          title={`Go to ${tenant.name}`}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto w-auto p-1 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(tenant.id);
          }}
          title="Delete tenant"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// Cancel + submit pair shared by the create and edit tenant dialogs.
function DialogFooterButtons({
  onCancel,
  onSubmit,
  disabled,
  label,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <>
      <Button variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button onClick={onSubmit} disabled={disabled}>
        {label}
      </Button>
    </>
  );
}

interface CreateTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  onError: (msg: string | null) => void;
}

function CreateTenantDialog({ open, onOpenChange, onCreated, onError }: CreateTenantDialogProps) {
  const [tenantName, setTenantName] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [creatingTenant, setCreatingTenant] = useState(false);

  // Reset fields each time the dialog opens.
  useEffect(() => {
    if (open) {
      setTenantName("");
      setTenantSlug("");
      setSlugTouched(false);
    }
  }, [open]);

  const handleCreateTenant = async () => {
    const name = tenantName.trim();
    const slug = tenantSlug.trim() || slugify(name);
    if (!name) return;
    setCreatingTenant(true);
    onError(null);
    try {
      await portalFetch("/api/portal/tenants", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, slug }),
      });
      onOpenChange(false);
      onCreated();
    } catch (err: unknown) {
      onError(formatZodError(err));
    }
    setCreatingTenant(false);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Tenant"
      description="Create a new tenant workspace. Each tenant has isolated dashboards, users, and settings."
      footer={
        <DialogFooterButtons
          onCancel={() => onOpenChange(false)}
          onSubmit={() => void handleCreateTenant()}
          disabled={creatingTenant || !tenantName.trim()}
          label={creatingTenant ? "Creating..." : "Create Tenant"}
        />
      }
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="create-tenant-name">Name</Label>
          <Input
            id="create-tenant-name"
            value={tenantName}
            onChange={(e) => {
              setTenantName(e.target.value);
              if (!slugTouched) setTenantSlug(slugify(e.target.value));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void handleCreateTenant();
              }
            }}
            placeholder="Tenant name"
            type="text"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- inside <FormDialog>; focus tenant name field on open
            autoFocus
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="create-tenant-slug">URL slug</Label>
          <Input
            id="create-tenant-slug"
            value={tenantSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setTenantSlug(e.target.value);
            }}
            placeholder="URL slug (auto-filled from name)"
            type="text"
          />
        </div>
      </div>
    </FormDialog>
  );
}

interface EditTenantDialogProps {
  target: Tenant | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  onError: (msg: string | null) => void;
}

// Slug input for the edit dialog, plus the URL-change warning shown only when
// the slug diverges from the tenant's original slug. Extracted so
// EditTenantDialog stays under the portal's max-lines-per-function cap.
function EditSlugField({
  slug,
  changed,
  onChange,
}: {
  slug: string;
  changed: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor="edit-tenant-slug">URL slug</Label>
      <Input
        id="edit-tenant-slug"
        value={slug}
        onChange={(e) => onChange(e.target.value)}
        placeholder="URL slug"
        type="text"
      />
      {changed && (
        <p className="text-xs text-warning">
          Changing the slug changes this tenant's URL — existing links and bookmarks will break.
        </p>
      )}
    </div>
  );
}

function EditTenantDialog({ target, onOpenChange, onSaved, onError }: EditTenantDialogProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  // Prefill from the selected tenant each time a new tenant is targeted.
  useEffect(() => {
    if (target) {
      setName(target.name);
      setSlug(target.slug);
      setSlugTouched(false);
    }
  }, [target]);

  const originalSlug = target?.slug ?? "";
  const trimmedSlug = slug.trim();
  const slugChanged = target !== null && trimmedSlug !== originalSlug;

  const handleSave = async () => {
    if (!target) return;
    const trimmedName = name.trim();
    if (!trimmedName || !trimmedSlug) return;
    setSaving(true);
    onError(null);
    try {
      await portalFetch(`/api/portal/tenants/${target.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: target.id, name: trimmedName, slug: trimmedSlug }),
      });
      onOpenChange(false);
      onSaved();
    } catch (err: unknown) {
      onError(formatZodError(err));
    }
    setSaving(false);
  };

  return (
    <FormDialog
      open={target !== null}
      onOpenChange={onOpenChange}
      title="Edit Tenant"
      description="Update this tenant's display name and URL slug."
      footer={
        <DialogFooterButtons
          onCancel={() => onOpenChange(false)}
          onSubmit={() => void handleSave()}
          disabled={saving || !name.trim() || !trimmedSlug}
          label={saving ? "Saving..." : "Save Changes"}
        />
      }
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="edit-tenant-name">Name</Label>
          <Input
            id="edit-tenant-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // Only auto-fill the slug from the name while the user hasn't
              // touched the slug field themselves.
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void handleSave();
              }
            }}
            placeholder="Tenant name"
            type="text"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- inside <FormDialog>; focus name field on open
            autoFocus
          />
        </div>
        <EditSlugField
          slug={slug}
          changed={slugChanged}
          onChange={(value) => {
            setSlugTouched(true);
            setSlug(value);
          }}
        />
      </div>
    </FormDialog>
  );
}

interface DeleteTenantDialogProps {
  target: { id: string; name: string } | null;
  onClose: () => void;
  onConfirm: (tenantId: string) => Promise<void>;
}

// Live deletion-preview fetch shared by the Remove (soft-delete) and Purge
// (hard-delete) confirm dialogs — both surface the same blast-radius counts.
// Scoped to the targeted tenant so a stale response for a previous target is
// dropped (the `d?.tenantId === id` guard).
function useDeletionPreview(target: { id: string; name: string } | null): DeleteDialogState | null {
  const [state, setState] = useState<DeleteDialogState | null>(null);

  useEffect(() => {
    if (!target) {
      setState(null);
      return;
    }
    const { id, name } = target;
    setState({ tenantId: id, tenantName: name, preview: null, loading: true, previewError: null });
    portalFetch<TenantDeletionPreview>(`/api/portal/tenants/${id}/deletion-preview`)
      .then((preview) =>
        setState((d) => (d?.tenantId === id ? { ...d, preview, loading: false } : d)),
      )
      .catch((err: unknown) =>
        setState((d) =>
          d?.tenantId === id
            ? {
                ...d,
                loading: false,
                previewError: formatZodError(err),
              }
            : d,
        ),
      );
  }, [target]);

  return state;
}

// Confirm dialog for a soft delete (Remove). The tenant is hidden + recoverable;
// the preview lists what will be hidden.
function DeleteTenantDialog({ target, onClose, onConfirm }: DeleteTenantDialogProps) {
  const state = useDeletionPreview(target);

  return (
    <AlertDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove "{state?.tenantName}"?</AlertDialogTitle>
          <AlertDialogDescription>
            This tenant will be hidden and recoverable from Deleted tenants. Its data stays intact
            until you restore or purge it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {state && <DeletionPreviewBody dialog={state} mode="remove" />}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (state) void onConfirm(state.tenantId);
            }}
            disabled={state?.loading ?? false}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Remove tenant
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface PurgeTenantDialogProps {
  target: { id: string; name: string } | null;
  onClose: () => void;
  onConfirm: (tenantId: string) => Promise<void>;
}

// SIM-2320: permanent hard-delete confirm for an already-soft-deleted tenant.
// Same blast-radius preview as Remove, but irreversible — the copy + destructive
// action make clear the tenant cannot be restored afterward.
function PurgeTenantDialog({ target, onClose, onConfirm }: PurgeTenantDialogProps) {
  const state = useDeletionPreview(target);

  return (
    <AlertDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete "{state?.tenantName}"?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes the tenant and everything attached to it. This cannot be undone
            — the tenant cannot be restored afterward.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {state && <DeletionPreviewBody dialog={state} mode="purge" />}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (state) void onConfirm(state.tenantId);
            }}
            disabled={state?.loading ?? false}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Copy that differs between the Remove (soft) and Purge (hard) preview dialogs.
// The blast-radius row list is identical; only the framing verbs change.
const PREVIEW_COPY = {
  remove: {
    loading: "Loading what will be hidden…",
    header: "This will hide:",
    empty:
      "This tenant has no attached data — nothing will be hidden beyond the tenant record itself.",
  },
  purge: {
    loading: "Loading what will be permanently deleted…",
    header: "This will permanently delete:",
    empty: "This tenant has no attached data — only the tenant record will be removed.",
  },
} as const;

function DeletionPreviewBody({
  dialog,
  mode,
}: {
  dialog: DeleteDialogState;
  mode: "remove" | "purge";
}) {
  const rows = dialog.preview ? previewRows(dialog.preview) : [];
  const hasAnyContent = rows.length > 0;
  const copy = PREVIEW_COPY[mode];
  return (
    <div className="text-sm">
      {dialog.loading && (
        <p className="text-muted-foreground italic" data-testid="deletion-preview-loading">
          {copy.loading}
        </p>
      )}
      {dialog.previewError && (
        <p className="text-destructive" data-testid="deletion-preview-error">
          Could not load preview: {dialog.previewError}
        </p>
      )}
      {dialog.preview && hasAnyContent && (
        <>
          <p className="text-foreground font-medium mb-2">{copy.header}</p>
          <ul
            className="list-disc list-inside space-y-1 text-foreground"
            data-testid="deletion-preview-list"
          >
            {rows.map((row) => (
              <li key={row}>{row}</li>
            ))}
          </ul>
        </>
      )}
      {dialog.preview && !hasAnyContent && (
        <p className="text-muted-foreground italic" data-testid="deletion-preview-empty">
          {copy.empty}
        </p>
      )}
    </div>
  );
}

interface TenantDataApi {
  tenants: Tenant[];
  fetchTenants: () => void;
  deleteTenant: (tenantId: string) => Promise<void>;
}

// Owns the tenant list plus its fetch / delete network actions. Each fetcher is
// stable (useCallback over stable setters + the injected callbacks), so the
// mount effect has honest deps and needs no eslint-disable.
function useTenantData(
  onError: (msg: string | null) => void,
  onLoadingChange: (loading: boolean) => void,
): TenantDataApi {
  const { refetch: refetchTenants } = useTenant();
  const [tenants, setTenants] = useState<Tenant[]>([]);

  const fetchTenants = useCallback(async () => {
    onLoadingChange(true);
    try {
      setTenants(await portalFetch<Tenant[]>("/api/portal/tenants"));
    } catch (err: unknown) {
      onError(formatZodError(err));
    }
    onLoadingChange(false);
  }, [onError, onLoadingChange]);

  useEffect(() => {
    void fetchTenants();
  }, [fetchTenants]);

  const refresh = () => {
    void fetchTenants();
    refetchTenants();
  };

  const deleteTenant = async (tenantId: string): Promise<void> => {
    onError(null);
    await portalFetch(`/api/portal/tenants/${tenantId}`, { method: "DELETE" });
    refresh();
  };

  return { tenants, fetchTenants: refresh, deleteTenant };
}

interface DeletedTenantRowProps {
  tenant: DeletedTenant;
  onRestore: (tenantId: string) => void;
  onPurge: (tenant: { id: string; name: string }) => void;
  restoring: boolean;
}

// SIM-2320: one soft-deleted tenant, with the removal date, a Restore action,
// and a Purge action (permanent hard-delete, confirmed via PurgeTenantDialog).
function DeletedTenantRow({ tenant, onRestore, onPurge, restoring }: DeletedTenantRowProps) {
  return (
    <li className="flex items-center justify-between px-4 py-3 text-sm">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <School className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="font-medium truncate">{tenant.name}</span>
        <span className="text-muted-foreground text-xs truncate">{tenant.slug}</span>
        <span className="text-muted-foreground text-xs ml-auto whitespace-nowrap">
          removed {new Date(tenant.deletedAt).toLocaleDateString()}
        </span>
      </div>
      <div className="flex items-center gap-2 ml-3 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRestore(tenant.id)}
          disabled={restoring}
          title={`Restore ${tenant.name}`}
        >
          <RotateCcw className="h-4 w-4" /> {restoring ? "Restoring…" : "Restore"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-destructive"
          onClick={() => onPurge({ id: tenant.id, name: tenant.name })}
          disabled={restoring}
          title={`Permanently delete ${tenant.name}`}
        >
          <Trash2 className="h-4 w-4" /> Purge
        </Button>
      </div>
    </li>
  );
}

// SIM-2320: owns the soft-deleted-tenant list plus its fetch / restore / purge
// actions. Restoring refetches both this list and the active tenant list (via
// onChanged) so the tenant moves from Deleted back to Tenants in one action;
// purging refetches this list (the tenant is gone for good).
function useDeletedTenantData(
  onError: (msg: string | null) => void,
  onChanged: () => void,
): {
  deleted: DeletedTenant[];
  fetchDeleted: () => Promise<void>;
  restoreTenant: (tenantId: string) => Promise<void>;
  purgeTenant: (tenantId: string) => Promise<void>;
} {
  const [deleted, setDeleted] = useState<DeletedTenant[]>([]);

  const fetchDeleted = useCallback(async () => {
    try {
      setDeleted(await portalFetch<DeletedTenant[]>("/api/portal/tenants/deleted"));
    } catch (err: unknown) {
      onError(formatZodError(err));
    }
  }, [onError]);

  useEffect(() => {
    void fetchDeleted();
  }, [fetchDeleted]);

  const restoreTenant = async (tenantId: string): Promise<void> => {
    onError(null);
    await portalFetch(`/api/portal/tenants/${tenantId}/restore`, { method: "POST" });
    await fetchDeleted();
    onChanged();
  };

  const purgeTenant = async (tenantId: string): Promise<void> => {
    onError(null);
    await portalFetch(`/api/portal/tenants/${tenantId}/purge`, { method: "DELETE" });
    await fetchDeleted();
  };

  return { deleted, fetchDeleted, restoreTenant, purgeTenant };
}

interface DeletedTenantsSectionProps {
  // Bumped by the parent after a soft delete; the section refetches when it changes.
  refreshKey: number;
  onError: (msg: string | null) => void;
  onRestored: () => void;
}

// SIM-2320: collapsible recovery surface listing soft-deleted tenants. Hidden
// entirely when there are none, so it adds no clutter for the common case.
function DeletedTenantsSection({ refreshKey, onError, onRestored }: DeletedTenantsSectionProps) {
  const { deleted, fetchDeleted, restoreTenant, purgeTenant } = useDeletedTenantData(
    onError,
    onRestored,
  );
  const [open, setOpen] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<{ id: string; name: string } | null>(null);

  // Refetch whenever the parent signals a change (e.g. a tenant was just removed).
  useEffect(() => {
    if (refreshKey > 0) void fetchDeleted();
  }, [refreshKey, fetchDeleted]);

  const handleRestore = async (tenantId: string) => {
    setRestoringId(tenantId);
    try {
      await restoreTenant(tenantId);
    } catch (err: unknown) {
      onError(formatZodError(err));
    }
    setRestoringId(null);
  };

  const handlePurgeConfirm = async (tenantId: string) => {
    try {
      await purgeTenant(tenantId);
    } catch (err: unknown) {
      onError(formatZodError(err));
    }
    setPurgeTarget(null);
  };

  if (deleted.length === 0) return null;

  return (
    <div className="mt-6">
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Trash2 className="h-4 w-4" /> Deleted tenants ({deleted.length})
      </Button>
      {open && (
        <ul className="mt-2 border border-border rounded divide-y divide-border">
          {deleted.map((t) => (
            <DeletedTenantRow
              key={t.id}
              tenant={t}
              onRestore={(id) => void handleRestore(id)}
              onPurge={setPurgeTarget}
              restoring={restoringId === t.id}
            />
          ))}
        </ul>
      )}
      <PurgeTenantDialog
        target={purgeTarget}
        onClose={() => setPurgeTarget(null)}
        onConfirm={handlePurgeConfirm}
      />
    </div>
  );
}

interface TenantsSectionProps {
  onError: (msg: string | null) => void;
  onLoadingChange: (loading: boolean) => void;
}

export function TenantsSection({ onError, onLoadingChange }: TenantsSectionProps) {
  const navigate = useNavigate();
  const { tenants, fetchTenants, deleteTenant } = useTenantData(onError, onLoadingChange);

  const [tenantOpen, setTenantOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Tenant | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  // Bumped after a soft delete so the Deleted-tenants section refetches and the
  // just-removed tenant appears there.
  const [deletedRefreshKey, setDeletedRefreshKey] = useState(0);

  const handleDeleteTenant = (tenantId: string) => {
    const tenant = tenants.find((t) => t.id === tenantId);
    if (tenant) setDeleteTarget({ id: tenant.id, name: tenant.name });
  };

  const handleDeleteConfirm = async (tenantId: string) => {
    try {
      await deleteTenant(tenantId);
      setDeletedRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      onError(formatZodError(err));
    }
    setDeleteTarget(null);
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <School className="h-5 w-5" /> Tenants
        </h2>
        <Button size="sm" onClick={() => setTenantOpen(true)}>
          <Plus className="h-4 w-4" /> Add Tenant
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Each tenant is an isolated workspace with its own dashboards, users, and settings.
      </p>

      {tenants.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tenants found.</p>
      ) : (
        <ul className="border border-border rounded divide-y divide-border">
          {tenants.map((t) => (
            <TenantRow
              key={t.id}
              tenant={t}
              onEdit={setEditTarget}
              onNavigate={(slug) => void navigate(`/${slug}`)}
              onDelete={handleDeleteTenant}
            />
          ))}
        </ul>
      )}

      <CreateTenantDialog
        open={tenantOpen}
        onOpenChange={setTenantOpen}
        onCreated={fetchTenants}
        onError={onError}
      />

      <EditTenantDialog
        target={editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
        onSaved={fetchTenants}
        onError={onError}
      />

      <DeleteTenantDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      <DeletedTenantsSection
        refreshKey={deletedRefreshKey}
        onError={onError}
        onRestored={fetchTenants}
      />
    </section>
  );
}
